// Shapes returned by the driver_loan_* functions (db/migrations/0360), and the
// few display helpers the Driver Loans page shares. No figure is computed here:
// balances, schedules and paydays all arrive from Postgres. CLAUDE.md rule 4.

export type Category = {
  code: string
  label: string
  description: string
  needs_phone: boolean
  pays_out: boolean
  vehicle_rule: 'required' | 'optional'
}

export type DriverOption = {
  id: string
  name: string
  status: string
  phone: string | null
  has_email: boolean
  manager: string | null
  last_vehicle_id: string | null
}

export type Options = {
  can_record_pay: boolean
  /** May release payments and mark a signed loan paid (db/migrations/0361). */
  can_release: boolean
  releasers: string
  proof_address: string
  next_payday: string
  next_pay_month: string
  categories: Category[]
  drivers: DriverOption[]
  vehicles: { id: string; label: string }[]
}

export type ScheduleRow = { no: number; payday: string; pay_month: string; scheduled_zar: number; recorded_zar: number }

export type Loan = {
  id: string
  ref: string
  status: 'sending' | 'send_failed' | 'awaiting_signature' | 'signed' | 'declined' | 'cancelled'
  driver_id: string
  driver_name: string
  category: string
  category_label: string
  pays_out: boolean
  amount_zar: number
  reason: string
  phone_number: string | null
  vehicle: string | null
  no_vehicle: boolean
  event_date: string | null
  instalments: number
  terms_reason: string | null
  requested_by_name: string
  requested_at: string
  sent_at: string | null
  driver_email: string | null
  send_error: string | null
  signed_at: string | null
  declined_at: string | null
  last_checked_at: string | null
  first_payday: string | null
  paid_out_at: string | null
  paid_out_note: string | null
  paid_out_by_name: string | null
  paid_out_method: 'marked' | 'emailed_proof' | null
  notified_at: string | null
  proofs: { id: string; filename: string | null; received_at: string | null; from_email: string | null }[]
  cancelled_at: string | null
  cancel_reason: string | null
  recovered_zar: number
  balance_zar: number | null
  has_document: boolean
  schedule: ScheduleRow[] | null
}

export type ListResult = {
  rows: Loan[]
  counts: { awaiting_signature: number; send_failed: number; to_pay_out: number; signed: number; outstanding_zar: number }
}

export type DueResult = {
  pay_month: string
  payday: string
  rows: {
    loan_id: string
    ref: string
    driver_name: string
    category_label: string
    simplepay_item: string
    due_zar: number
    carried_from_earlier: boolean
    recorded_this_month: number
  }[]
}

export function formatZar(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  // en-US and not en-ZA, deliberately: en-ZA writes the decimal as a comma, and
  // a thousands swap then reads R150,00 as "R150 00". This matches the page the
  // driver signs, which Postgres writes as R4 500.00.
  return 'R' + ZAR.format(n).replace(/,/g, ' ')
}

const ZAR = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Times arrive in UTC and are always shown in Johannesburg time. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric' })
}

/** A phone stored as +27... is shown and typed the way a manager reads it: 0... */
export function localPhone(p: string | null | undefined): string {
  const s = String(p ?? '').replace(/[\s\-()]/g, '')
  if (/^\+27\d{9}$/.test(s)) return '0' + s.slice(3)
  if (/^27\d{9}$/.test(s)) return '0' + s.slice(2)
  return s
}

export const STATUS_LABEL: Record<Loan['status'], string> = {
  sending: 'Sending',
  send_failed: 'Not sent',
  awaiting_signature: 'Waiting for his signature',
  signed: 'Signed',
  declined: 'Declined by the driver',
  cancelled: 'Cancelled',
}

export const STATUS_TONE: Record<Loan['status'], 'green' | 'red' | 'amber' | 'neutral' | 'brand'> = {
  sending: 'amber',
  send_failed: 'red',
  awaiting_signature: 'amber',
  signed: 'green',
  declined: 'red',
  cancelled: 'neutral',
}

export function pdfBlobUrl(base64: string): string {
  const bin = atob(base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
}
